import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Search,
  UserPlus,
  Users,
  ChevronRight,
  Trash2,
  X,
  Eye,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Card } from '@/components/common/Card'
import { EmptyState, Loader, SkeletonRow } from '@/components/common/Loader'
import { PatientAvatar } from '@/components/patient/PatientAvatar'
import { Modal } from '@/components/common/Modal'
import { Badge } from '@/components/common/Badge'
import { usePatientStore } from '@/store/patientStore'
import { patientApi } from '@/api/patientApi'
import { formatDate, formatMobile, humanize, calculateAge, cn } from '@/lib/utils'
import toast from 'react-hot-toast'

export default function PatientList() {
  const navigate = useNavigate()
  const {
    patients,
    loading,
    search,
    setSearch,
    fetchList,
    nextPage,
    hasMore,
    remove,
  } = usePatientStore()

  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await patientApi.remove(deleteTarget.patient_id)
      remove(deleteTarget.patient_id)
      toast.success('Patient deleted')
      setDeleteTarget(null)
    } catch {
      // toast handled by interceptor
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="container-page py-6 sm:py-8 lg:py-10">
      <PageHeader
        eyebrow="Patient management"
        title="All patients"
        description="Search, sort, and open a patient's full timeline."
        actions={
          <Button
            onClick={() => navigate('/patients/new')}
            leftIcon={<UserPlus className="h-4 w-4" />}
          >
            New patient
          </Button>
        }
      />

      {/* Search bar */}
      <Card className="mt-6 !p-3">
        <div className="flex items-center gap-2 rounded-xl bg-ink-50/40 px-3 ring-1 ring-inset ring-ink-200 focus-within:ring-2 focus-within:ring-primary-500">
          <Search className="h-4 w-4 text-ink-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, mobile, or patient ID…"
            className="h-11 w-full bg-transparent text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="rounded-md p-1 text-ink-400 hover:bg-white hover:text-ink-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="mt-4 overflow-hidden">
        {loading && patients.length === 0 ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : patients.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title={search ? `No patients match “${search}”` : 'No patients yet'}
            description={
              search
                ? 'Try a different name, mobile number or patient ID.'
                : 'Register your first patient to start building the clinic record.'
            }
            action={
              !search && (
                <Button onClick={() => navigate('/patients/new')}>
                  Register a patient
                </Button>
              )
            }
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead className="bg-ink-50/60">
                  <tr>
                    <th className="px-4 py-3 text-left label-text">Patient</th>
                    <th className="px-4 py-3 text-left label-text">ID</th>
                    <th className="px-4 py-3 text-left label-text">Age / Sex</th>
                    <th className="px-4 py-3 text-left label-text">Mobile</th>
                    <th className="px-4 py-3 text-left label-text">Registered</th>
                    <th className="px-4 py-3 text-right label-text">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((p) => (
                    <tr
                      key={p.patient_id}
                      className="border-t border-ink-100 transition hover:bg-primary-50/30"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <PatientAvatar name={p.name} id={p.patient_id} size="sm" />
                          <div className="min-w-0">
                            <Link
                              to={`/patients/${p.patient_id}`}
                              className="block truncate font-semibold text-ink-900 hover:text-primary-800"
                            >
                              {p.name}
                            </Link>
                            <p className="truncate text-xs text-ink-500">
                              {p.occupation || '—'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs font-bold text-primary-700">
                        {p.patient_id}
                      </td>
                      <td className="px-4 py-3 text-ink-700">
                        {p.age ?? calculateAge(p.dob)} yrs · {humanize(p.gender)}
                      </td>
                      <td className="px-4 py-3 text-ink-700">
                        {formatMobile(p.mobile_number)}
                      </td>
                      <td className="px-4 py-3 text-ink-700">
                        {formatDate(p.registration_date || p.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/patients/${p.patient_id}`)}
                            title="Open"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(p)}
                            title="Delete"
                            className="text-rose-600 hover:bg-rose-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <ul className="divide-y divide-ink-100 md:hidden">
              {patients.map((p) => (
                <li key={p.patient_id} className="px-4 py-3">
                  <Link
                    to={`/patients/${p.patient_id}`}
                    className="flex items-center gap-3"
                  >
                    <PatientAvatar name={p.name} id={p.patient_id} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-ink-900">{p.name}</p>
                      <p className="truncate text-xs text-ink-500">
                        {p.patient_id} · {p.age ?? calculateAge(p.dob)} yrs · {humanize(p.gender)}
                      </p>
                      <p className="truncate text-xs text-ink-500">
                        {formatMobile(p.mobile_number)}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-ink-400" />
                  </Link>
                </li>
              ))}
            </ul>

            {/* Pagination footer */}
            <div className="flex items-center justify-between border-t border-ink-100 bg-ink-50/40 px-4 py-3">
              <p className="text-xs text-ink-500">
                Showing <span className="font-semibold text-ink-700">{patients.length}</span>{' '}
                patient{patients.length === 1 ? '' : 's'}
              </p>
              {hasMore && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => nextPage()}
                  loading={loading}
                >
                  Load more
                </Button>
              )}
            </div>
          </>
        )}
      </Card>

      {/* Delete confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete patient?"
        description="This permanently removes the patient and every visit. This cannot be undone."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete} loading={deleting}>
              Yes, delete
            </Button>
          </>
        }
      >
        {deleteTarget && (
          <div className="flex items-center gap-3 rounded-xl bg-rose-50/60 p-3 ring-1 ring-rose-100">
            <PatientAvatar name={deleteTarget.name} id={deleteTarget.patient_id} />
            <div>
              <p className="font-semibold text-ink-900">{deleteTarget.name}</p>
              <p className="text-xs text-ink-500">
                {deleteTarget.patient_id} · {formatMobile(deleteTarget.mobile_number)}
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
